import { Lockup, Mark } from "../components/Mark";
import { Page, SectionBlock } from "../components/Page";

export function Logo() {
  return (
    <Page
      number="03"
      title="Logo system"
      eyebrow="The wordmark"
      intro={
        <>
          Our mark is a single Devanagari syllable — अ — paired with a wordmark
          and a tagline. The syllable means the beginning, the first sound, the
          unspoken root. The mark itself is fixed in amber across every variant;
          the page around it changes, the mark does not.
        </>
      }
    >
      <SectionBlock title="The mark">
        <div
          className="surface flex flex-col items-center gap-10 px-7 py-16"
          style={{ background: "var(--paper)" }}
        >
          <Mark size={220} />
          <p className="max-w-md text-center font-body text-[14px] leading-relaxed text-ink-soft">
            The canonical asset is{" "}
            <code className="font-display text-[13px] text-amber">
              /logo.png
            </code>{" "}
            — always used at this colour. Reach for the PNG for any real
            surface: marketing posters, social posts, decks, email signatures,
            business cards. The font-rendered Unicode glyph is only acceptable
            when a PNG can't be placed (small UI chrome, plain-text contexts).
          </p>
        </div>
      </SectionBlock>

      <SectionBlock title="Full lockup">
        <div
          className="surface flex flex-col items-center gap-10 px-7 py-16"
          style={{ background: "var(--paper)" }}
        >
          <Lockup glyphSize={84} wordmarkSize={20} taglineSize={16} />
          <p className="max-w-md text-center font-body text-[14px] leading-relaxed text-ink-soft">
            The default lockup. Use this on the website, the brand book, email
            headers, document covers. Keep clear space around it equal to the
            height of the glyph.
          </p>
        </div>
      </SectionBlock>

      <SectionBlock title="Sizes">
        <div className="flex flex-col gap-10">
          {[
            {
              size: 120,
              role: "Hero",
              note: "Website hero, document covers, posters.",
            },
            {
              size: 72,
              role: "Header",
              note: "Email header, nav bars, page titles.",
            },
            {
              size: 48,
              role: "Footer",
              note: "Footers, captions, secondary contexts.",
            },
            {
              size: 28,
              role: "Inline",
              note: "Smallest acceptable — chips, in-paragraph references.",
            },
          ].map(({ size, role, note }) => (
            <div
              key={role}
              className="flex flex-col gap-4 border-t pt-8 first:border-t-0 first:pt-0 border-rule md:flex-row md:items-center md:gap-10"
            >
              <div className="md:w-36 md:shrink-0">
                <p className="eyebrow">{role}</p>
                <p className="mt-1 font-body text-[12px] tabular-nums text-ink-soft">
                  {size}px glyph
                </p>
              </div>
              <div className="min-w-0 flex-1">
                <Lockup
                  glyphSize={size}
                  wordmarkSize={Math.max(11, Math.round(size * 0.22))}
                  taglineSize={Math.max(10, Math.round(size * 0.18))}
                  showTagline={size >= 48}
                />
              </div>
              <p className="font-body text-[13px] text-ink-soft md:w-52 md:shrink-0">
                {note}
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="Clear space">
        <div className="grid gap-6 md:grid-cols-2">
          <div
            className="surface flex flex-col items-center justify-center p-12"
            style={{ background: "var(--paper)" }}
          >
            <div className="relative">
              <div
                className="absolute -inset-12 border border-dashed"
                style={{ borderColor: "var(--amber)" }}
              />
              <Lockup glyphSize={56} wordmarkSize={14} taglineSize={12} />
            </div>
            <p className="mt-12 text-center font-body text-[13px] text-ink-soft">
              Minimum padding equals one glyph-height on all sides.
            </p>
          </div>
          <div
            className="surface flex flex-col items-center justify-center p-12"
            style={{ background: "var(--ink)" }}
          >
            <Lockup
              glyphSize={56}
              wordmarkSize={14}
              taglineSize={12}
              tone="light"
            />
            <p
              className="mt-12 text-center font-body text-[13px]"
              style={{ color: "var(--peach)" }}
            >
              On ink backgrounds the mark stays unchanged. Wordmark goes to
              paper; tagline goes to peach.
            </p>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Do / don't">
        <div className="grid gap-5 md:grid-cols-2">
          {[
            {
              ok: true,
              l: "Use the PNG asset whenever you can place an image.",
            },
            {
              ok: true,
              l: "Pair with Vihangam Yog set in body face, all-caps, wide-tracked.",
            },
            {
              ok: true,
              l: "Keep the tagline in display italic, ink-soft (or peach on dark).",
            },
            {
              ok: true,
              l: "Lock the mark to the wordmark — never set them on separate lines.",
            },
            {
              ok: false,
              l: "Don't substitute a stylised English ‘A’ or any other glyph.",
            },
            {
              ok: false,
              l: "Don't recolour the mark. It is always amber, even on dark surfaces.",
            },
            {
              ok: false,
              l: "Don't stretch, condense, outline, or drop-shadow the mark.",
            },
            {
              ok: false,
              l: "Don't place the lockup on busy photography without a paper underlay.",
            },
          ].map((d) => (
            <div
              key={d.l}
              className="border-l-2 pl-4"
              style={{
                borderColor: d.ok ? "var(--amber)" : "var(--ink-soft)",
              }}
            >
              <p
                className="eyebrow"
                style={{
                  color: d.ok ? "var(--amber)" : "var(--ink-soft)",
                }}
              >
                {d.ok ? "Do" : "Don't"}
              </p>
              <p className="mt-2 font-body text-[15px] text-ink">{d.l}</p>
            </div>
          ))}
        </div>
      </SectionBlock>
    </Page>
  );
}
